import axios from 'axios';
import { showMessage } from 'app/store/actions/fuse';
import { API_BASE_URL } from '@config';

// Action Types
export const SET_LLM_CONFIG = 'SET_LLM_CONFIG';
export const SET_LLM_CONFIG_LOADING = 'SET_LLM_CONFIG_LOADING';
export const SET_LLM_CONFIG_ERROR = 'SET_LLM_CONFIG_ERROR';
export const SET_LLM_VALIDATION_RESULT = 'SET_LLM_VALIDATION_RESULT';
export const SET_LLM_RATE_LIMIT = 'SET_LLM_RATE_LIMIT';
export const SET_LLM_CHAT_LOADING = 'SET_LLM_CHAT_LOADING';

// Action Creators
export const setLLMConfig = (data) => ({
  type: SET_LLM_CONFIG,
  payload: data,
});

export const setLLMConfigLoading = (isLoading) => ({
  type: SET_LLM_CONFIG_LOADING,
  payload: isLoading,
});

export const setLLMConfigError = (error) => ({
  type: SET_LLM_CONFIG_ERROR,
  payload: error,
});

export const setLLMValidationResult = (result) => ({
  type: SET_LLM_VALIDATION_RESULT,
  payload: result,
});

export const setLLMRateLimit = (data) => ({
  type: SET_LLM_RATE_LIMIT,
  payload: data,
});

export const setLLMChatLoading = (isLoading) => ({
  type: SET_LLM_CHAT_LOADING,
  payload: isLoading,
});

/**
 * Get LLM status (consumer-facing, requires llm:chat permission)
 * Returns only enabled status and provider name
 */
export const getLLMStatus = () => (dispatch) => {
  const url = `${API_BASE_URL}/api/llm-providers/status`;

  return axios
    .get(url)
    .then((response) => {
      dispatch(setLLMConfig(response.data));
      return response.data;
    })
    .catch(() => {
      // Silently fail - user may not have llm:chat permission
    });
};

/**
 * Get full LLM provider configuration (admin only, requires settings:read)
 */
export const getLLMConfig = () => (dispatch) => {
  dispatch(setLLMConfigLoading(true));
  const url = `${API_BASE_URL}/api/llm-providers/config`;

  return axios
    .get(url)
    .then((response) => {
      dispatch(setLLMConfig(response.data));
      dispatch(setLLMConfigLoading(false));
      return response.data;
    })
    .catch((err) => {
      dispatch(setLLMConfigLoading(false));
      dispatch(setLLMConfigError(err.response?.data?.message || 'Failed to load LLM configuration'));
      throw err;
    });
};

/**
 * Update LLM provider configuration (admin only)
 */
export const updateLLMConfig = (config) => (dispatch) => {
  dispatch(setLLMConfigLoading(true));
  const url = `${API_BASE_URL}/api/llm-providers/config`;

  return axios
    .put(url, config)
    .then((response) => {
      dispatch(showMessage({ message: 'LLM provider configuration updated successfully' }));
      dispatch(setLLMConfig(response.data.config));
      dispatch(setLLMConfigLoading(false));
      return response.data;
    })
    .catch((err) => {
      dispatch(setLLMConfigLoading(false));
      const errorMsg = err.response?.data?.message || 'Failed to update LLM configuration';
      dispatch(showMessage({ message: errorMsg, variant: 'error' }));
      dispatch(setLLMConfigError(errorMsg));
      throw err;
    });
};

/**
 * Validate LLM provider credentials (admin only)
 */
export const validateLLMCredentials = (credentials) => (dispatch) => {
  dispatch(setLLMValidationResult({ loading: true, valid: null, message: null }));
  const url = `${API_BASE_URL}/api/llm-providers/validate`;

  return axios
    .post(url, credentials)
    .then((response) => {
      dispatch(setLLMValidationResult({ 
        loading: false, 
        valid: response.data.valid, 
        message: response.data.message 
      }));
      dispatch(showMessage({ message: 'Credentials validated successfully' }));
      return response.data;
    })
    .catch((err) => {
      const errorMsg = err.response?.data?.message || 'Credential validation failed';
      dispatch(setLLMValidationResult({ 
        loading: false, 
        valid: false, 
        message: errorMsg 
      }));
      dispatch(showMessage({ message: errorMsg, variant: 'error' }));
      throw err;
    });
};

/**
 * Get current user's rate limit status
 */
export const getLLMRateLimitStatus = () => (dispatch) => {
  const url = `${API_BASE_URL}/api/llm-providers/rate-limit`;

  return axios
    .get(url)
    .then((response) => {
      dispatch(setLLMRateLimit(response.data));
      return response.data;
    })
    .catch(() => {
      // Silently fail - non-critical status poll. The user may lack
      // llm:chat permission or have a transient/expired session; this
      // should never surface as an unhandled rejection in the console.
    });
};

/**
 * Send a chat message to the LLM provider
 */
export const sendChatMessage = (message, conversationHistory = []) => (dispatch) => {
  dispatch(setLLMChatLoading(true));
  const url = `${API_BASE_URL}/api/llm-providers/chat`;

  return axios
    .post(url, { message, conversationHistory })
    .then((response) => {
      dispatch(setLLMChatLoading(false));
      // Update rate limit after sending message
      dispatch(getLLMRateLimitStatus());
      return response.data;
    })
    .catch((err) => {
      dispatch(setLLMChatLoading(false));
      const errorMsg = err.response?.data?.message || 'Failed to send message';
      
      // Show specific error for rate limit
      if (err.response?.status === 429) {
        dispatch(showMessage({ 
          message: 'You have reached your daily message limit', 
          variant: 'warning' 
        }));
      } else if (err.response?.status === 403) {
        dispatch(showMessage({ 
          message: 'AI assistant is not currently enabled', 
          variant: 'warning' 
        }));
      } else {
        dispatch(showMessage({ message: errorMsg, variant: 'error' }));
      }
      
      throw err;
    });
};

/**
 * Send a chat message with streaming response (token by token)
 * Uses Server-Sent Events for real-time token delivery
 * @param {string} message - User message
 * @param {Array} conversationHistory - Previous messages for context
 * @param {Function} onToken - Callback(token) called for each token received
 * @param {Function} onComplete - Callback(fullResponse, metadata) called when done
 * @param {Function} onError - Callback(error) called on error
 * @returns {Function} Abort function to cancel the stream
 */
export const sendChatMessageStreaming = (message, conversationHistory = [], onToken, onComplete, onError) => (dispatch, getState) => {
  dispatch(setLLMChatLoading(true));
  const url = `${API_BASE_URL}/api/llm-providers/chat/stream`;
  
  // Get auth token - use the axios default header (set by jwtService) or localStorage
  const axiosToken = axios.defaults.headers.common['Authorization'];
  const token = axiosToken ? axiosToken.replace('Bearer ', '') : localStorage.getItem('token');
  
  if (!token) {
    dispatch(setLLMChatLoading(false));
    if (onError) onError(new Error('Not authenticated'));
    return () => {};
  }
  
  // Create abort controller for cancellation
  const abortController = new AbortController();
  
  // Use fetch with streaming support
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ message, conversationHistory }),
    signal: abortController.signal,
  })
    .then((response) => {
      if (!response.ok) {
        return response.json().then((data) => {
          throw new Error(data.message || 'Stream request failed');
        });
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      function processStream() {
        return reader.read().then(({ done, value }) => {
          if (done) {
            dispatch(setLLMChatLoading(false));
            dispatch(getLLMRateLimitStatus());
            return;
          }
          
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete SSE events
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              // Store event type for next data line
              // (handled inline below)
            } else if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.substring(6));
                
                // Check if this follows a specific event type
                // For simplicity, we detect by data content
                if (data.token !== undefined) {
                  // Token event
                  if (onToken && data.token) {
                    onToken(data.token);
                  }
                } else if (data.fullResponse !== undefined) {
                  // Done event
                  dispatch(setLLMChatLoading(false));
                  if (onComplete) {
                    onComplete(data.fullResponse, {
                      source: data.source,
                      sources: data.sources,
                      hasDbContext: data.hasDbContext,
                    });
                  }
                } else if (data.message && data.message.includes) {
                  // Error event
                  dispatch(setLLMChatLoading(false));
                  if (onError) {
                    onError(new Error(data.message));
                  }
                }
              } catch (parseErr) {
                // Skip malformed JSON
              }
            }
          }
          
          return processStream();
        });
      }
      
      return processStream();
    })
    .catch((err) => {
      dispatch(setLLMChatLoading(false));
      
      if (err.name === 'AbortError') {
        // Stream was cancelled by user
        return;
      }
      
      const errorMsg = err.message || 'Failed to stream message';
      
      if (errorMsg.includes('rate limit') || errorMsg.includes('daily limit')) {
        dispatch(showMessage({ 
          message: 'You have reached your daily message limit', 
          variant: 'warning' 
        }));
      } else if (errorMsg.includes('not enabled') || errorMsg.includes('not configured')) {
        dispatch(showMessage({ 
          message: 'AI assistant is not currently enabled', 
          variant: 'warning' 
        }));
      } else {
        dispatch(showMessage({ message: errorMsg, variant: 'error' }));
      }
      
      if (onError) {
        onError(err);
      }
    });
  
  // Return abort function
  return () => {
    abortController.abort();
    dispatch(setLLMChatLoading(false));
  };
};

/**
 * Get list of available LLM providers
 */
export const getAvailableLLMProviders = () => (dispatch) => {
  const url = `${API_BASE_URL}/api/llm-providers/providers`;

  return axios
    .get(url)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.error('Failed to get available providers:', err);
      throw err;
    });
};
